import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut as firebaseSignOut } from 'firebase/auth';
import { AlertCircle, CheckCircle2, Clock, Eye, EyeOff, FileText, Loader2, Lock, ShieldCheck } from 'lucide-react';
import { auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import driverApplicationService, { errorMessage } from '../../services/driverApplicationService';
import { formatDisplayDate } from '../../utils/dateInput';

const BRAND_NAME = 'Himil Fleet';
const BRAND_LOGO_SRC = '/Himil-logo.png';

const CHECKLIST = [
  "Driver's license (you'll photograph the front and back)",
  "Medical examiner's certificate (medical card)",
  'Social Security Number',
  'Addresses for the last 3 years',
  'Employers for the last 3 years (10 years if you have driven a commercial vehicle)',
  'Accidents and traffic violations in the last 3 years'
];

const firebaseErrorText = (code) => ({
  'auth/email-already-in-use': 'An account with this email already exists. Sign in instead.',
  'auth/weak-password': 'Choose a password with at least 8 characters.',
  'auth/wrong-password': 'That password is incorrect.',
  'auth/invalid-credential': 'That password is incorrect.',
  'auth/user-not-found': 'No account exists for this email yet. Create one below.',
  'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
  'auth/network-request-failed': 'Network error. Check your connection and try again.'
}[code] || null);

const Shell = ({ children, companyName }) => (
  <div className="min-h-screen bg-gray-50">
    <header className="border-b border-gray-200 bg-white">
      <div className="mx-auto flex h-14 max-w-3xl items-center gap-3 px-4 sm:px-6">
        <img src={BRAND_LOGO_SRC} alt={`${BRAND_NAME} logo`} className="h-8 w-8" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900">{companyName || BRAND_NAME}</p>
          <p className="text-xs text-gray-500">Driver Application</p>
        </div>
      </div>
    </header>
    <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-10">{children}</main>
  </div>
);

const ApplyInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, userData, refreshUserProfile, logout } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | invalid | expired | revoked
  const [mode, setMode] = useState('create'); // create | signin
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [autoAccepting, setAutoAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await driverApplicationService.getInvite(token);
        if (cancelled) return;
        setInvite(data);
        if (data.state === 'valid' || data.state === 'accepted') {
          setLoadState('ready');
          setMode(data.accountExists ? 'signin' : 'create');
        } else {
          setLoadState(data.state);
        }
      } catch (error) {
        if (cancelled) return;
        setLoadState(error?.code === 'INVITE_EXPIRED' ? 'expired' : error?.code === 'INVITE_REVOKED' ? 'revoked' : 'invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const finishAccept = useCallback(async () => {
    await driverApplicationService.acceptInvite(token);
    await refreshUserProfile();
    navigate('/apply', { replace: true });
  }, [token, refreshUserProfile, navigate]);

  // Already signed in as the invitee: accept silently.
  const signedInEmail = user?.email?.toLowerCase() || null;
  const inviteEmail = invite?.email?.toLowerCase() || null;
  const signedInAsInvitee = Boolean(signedInEmail && inviteEmail && signedInEmail === inviteEmail);

  useEffect(() => {
    if (loadState !== 'ready' || !signedInAsInvitee || autoAccepting) return;
    setAutoAccepting(true);
    finishAccept().catch((error) => {
      setFormError(errorMessage(error));
      setAutoAccepting(false);
    });
  }, [loadState, signedInAsInvitee, autoAccepting, finishAccept]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    if (!invite?.email) return;

    if (mode === 'create') {
      if (password.length < 8) {
        setFormError('Choose a password with at least 8 characters.');
        return;
      }
      if (password !== confirm) {
        setFormError('The passwords do not match.');
        return;
      }
    }

    setBusy(true);
    let createdUser = null;
    try {
      if (mode === 'create') {
        const credential = await createUserWithEmailAndPassword(auth, invite.email, password);
        createdUser = credential.user;
      } else {
        await signInWithEmailAndPassword(auth, invite.email, password);
      }
      await finishAccept();
    } catch (error) {
      const fbText = error?.code ? firebaseErrorText(error.code) : null;
      if (error?.code === 'auth/email-already-in-use') {
        setMode('signin');
      }
      if (createdUser && !fbText) {
        // Account was created but the invite could not be bound; remove the orphan so they can retry.
        try { await createdUser.delete(); } catch (deleteError) { await firebaseSignOut(auth).catch(() => null); }
      }
      setFormError(fbText || errorMessage(error));
      setBusy(false);
    }
  };

  if (loadState === 'loading') {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
          <Loader2 className="h-8 w-8 animate-spin text-brand-mid" aria-hidden="true" />
          <p className="mt-3 text-sm">Checking your invite…</p>
        </div>
      </Shell>
    );
  }

  if (loadState !== 'ready') {
    const copy = {
      expired: { title: 'This invite has expired', body: 'Invite links are valid for a limited time. Ask the company that invited you to send a new one. Anything you already saved is kept.' },
      revoked: { title: 'This invite was cancelled', body: 'The company withdrew this invite. If you think that is a mistake, contact them directly.' },
      invalid: { title: 'We could not find this invite', body: 'Check that you opened the full link from your email. If it still does not work, ask the company to resend it.' }
    }[loadState] || { title: 'Something went wrong', body: 'Please try again later.' };
    return (
      <Shell companyName={invite?.companyName}>
        <div className="mx-auto max-w-md rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <Clock className="h-6 w-6 text-amber-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-xl font-bold text-gray-900">{copy.title}</h1>
          <p className="mt-2 text-sm text-gray-600">{copy.body}</p>
          <Link to="/login" className="mt-6 inline-block text-sm font-medium text-brand-mid hover:text-brand-dark">Go to sign in</Link>
        </div>
      </Shell>
    );
  }

  if (user && !signedInAsInvitee) {
    return (
      <Shell companyName={invite.companyName}>
        <div className="mx-auto max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" aria-hidden="true" />
            <div>
              <h1 className="text-lg font-semibold text-gray-900">You're signed in as someone else</h1>
              <p className="mt-1 text-sm text-gray-600">
                This invite was sent to <strong>{invite.emailMasked}</strong>, but you are signed in as <strong>{user.email}</strong>{userData?.role ? ` (${userData.role})` : ''}.
                Sign out to accept the invite with the right account.
              </p>
            </div>
          </div>
          <button type="button" onClick={() => logout()} className="btn-primary mt-5 w-full">Sign out and continue</button>
        </div>
      </Shell>
    );
  }

  if (signedInAsInvitee) {
    return (
      <Shell companyName={invite.companyName}>
        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
          <Loader2 className="h-8 w-8 animate-spin text-brand-mid" aria-hidden="true" />
          <p className="mt-3 text-sm">Opening your application…</p>
          {formError && <p className="mt-3 text-sm text-red-600">{formError}</p>}
        </div>
      </Shell>
    );
  }

  return (
    <Shell companyName={invite.companyName}>
      <div className="grid gap-6 lg:grid-cols-5">
        <section className="lg:col-span-3">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
            Hi {invite.firstName || 'there'}, {invite.companyName} invited you to apply
          </h1>
          <p className="mt-2 text-sm text-gray-600 sm:text-base">
            This is a DOT driver employment application. It takes about 20 minutes, saves automatically as you go, and you can finish it later on any device.
          </p>
          {invite.message && (
            <blockquote className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Message from {invite.companyName}</p>
              <p className="mt-1 whitespace-pre-line">{invite.message}</p>
            </blockquote>
          )}

          <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <FileText className="h-4 w-4 text-brand-mid" aria-hidden="true" /> What you'll need
            </h2>
            <ul className="mt-3 space-y-2 text-sm text-gray-700">
              {CHECKLIST.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 flex items-start gap-2 text-xs text-gray-500">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
              Your information is encrypted and shared only with {invite.companyName}. Your Social Security Number is stored encrypted and never shown on screen after you enter it.
            </p>
          </div>
        </section>

        <section className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900">{mode === 'create' ? 'Create your login' : 'Sign in to continue'}</h2>
            <p className="mt-1 text-sm text-gray-600">
              {mode === 'create'
                ? 'You will use this to come back to your application and, once hired, to open your driver portal.'
                : 'Use the password you created for this application.'}
            </p>

            {formError && (
              <div role="alert" className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span>{formError}</span>
              </div>
            )}

            <div className="mt-4">
              <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700">Email</label>
              <div className="relative mt-1">
                <input id="invite-email" type="email" value={invite.email || ''} readOnly className="block w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-3 pr-9 text-base text-gray-700 sm:py-2 sm:text-sm" />
                <Lock className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              </div>
              <p className="mt-1 text-xs text-gray-500">Locked to the address the invite was sent to.</p>
            </div>

            <div className="mt-4">
              <label htmlFor="invite-password" className="block text-sm font-medium text-gray-700">{mode === 'create' ? 'Create a password' : 'Password'}</label>
              <div className="relative mt-1">
                <input
                  id="invite-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
                  required
                  minLength={mode === 'create' ? 8 : undefined}
                  className="block w-full rounded-md border border-gray-300 px-3 py-3 pr-11 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:py-2 sm:text-sm"
                />
                <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-gray-500 hover:text-gray-700" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {mode === 'create' && <p className="mt-1 text-xs text-gray-500">At least 8 characters.</p>}
            </div>

            {mode === 'create' && (
              <div className="mt-4">
                <label htmlFor="invite-confirm" className="block text-sm font-medium text-gray-700">Confirm password</label>
                <input
                  id="invite-confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-3 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:py-2 sm:text-sm"
                />
              </div>
            )}

            <button type="submit" disabled={busy} className="btn-primary mt-6 w-full py-3 text-base sm:py-2.5 sm:text-sm">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {mode === 'create' ? 'Create account & start' : 'Sign in & continue'}
            </button>

            <p className="mt-4 text-center text-sm text-gray-600">
              {mode === 'create' ? (
                <>Already created a password? <button type="button" onClick={() => { setMode('signin'); setFormError(''); }} className="font-medium text-brand-mid hover:text-brand-dark">Sign in</button></>
              ) : (
                <>
                  Need an account? <button type="button" onClick={() => { setMode('create'); setFormError(''); }} className="font-medium text-brand-mid hover:text-brand-dark">Create one</button>
                  <span className="mx-1 text-gray-300">·</span>
                  <Link to="/forgot-password" className="font-medium text-brand-mid hover:text-brand-dark">Forgot password?</Link>
                </>
              )}
            </p>
            {invite.expiresAt && (
              <p className="mt-3 text-center text-xs text-gray-500">Invite valid until {formatDisplayDate(new Date(invite.expiresAt))}</p>
            )}
          </form>
        </section>
      </div>
    </Shell>
  );
};

export default ApplyInvitePage;
